/**
 * Implementações de `MediaSource` (F4-T02, F4-T03, F4-T04).
 */
import type { Sign } from '@/types/catalog';
import type { MediaResolution, MediaSource } from '@/services/media/types';
import { resolveItemUrl, type MediaManifest } from '@/services/media/mediaManifest';

/**
 * Base da origem de mídia.
 *
 * O padrão é `/media`, servido junto do app. A existência da origem não é assumida:
 * quem decide é o `manifest.json` responder ou não (ver `MediaProvider`). Assim,
 * uma instalação sem vídeos baixados simplesmente não mostra botão de download.
 */
export const MEDIA_BASE_URL: string = (import.meta.env.VITE_MEDIA_BASE_URL ?? '/media').replace(
  /\/$/,
  '',
);

/**
 * YouTube — **somente reprodução online** (PLAN §10).
 *
 * `canDownload` devolve `false` sempre, e isso é a decisão arquitetural central do
 * projeto: não se automatiza download do YouTube. Esta origem existe para que um
 * sinal sem arquivo local continue assistível quando há rede.
 */
export class OnlineYoutubeMediaSource implements MediaSource {
  readonly id = 'youtube-online';

  canDownload(): boolean {
    return false;
  }

  resolve(sign: Sign): Promise<MediaResolution> {
    const { provider, embedUrl, watchUrl, externalId } = sign.media;
    if (provider !== 'youtube' || !externalId) return Promise.resolve({});

    return Promise.resolve({
      url: embedUrl ?? watchUrl ?? `https://www.youtube-nocookie.com/embed/${externalId}`,
    });
  }
}

/**
 * Arquivo MP4 vindo de origem autorizada e controlada (PLAN §24).
 *
 * Três formas de descobrir a URL, nesta ordem:
 *  1. manifest da origem de mídia, quando existir — é o único caminho que traz
 *     tamanho e SHA-256, tornando o download verificável (PLAN §24, §39);
 *  2. `media.authorizedDownloadUrl`, quando o próprio catálogo a informa;
 *  3. convenção sobre `VITE_MEDIA_BASE_URL`, usando o ID do sinal como nome —
 *     nunca o nome visível, que tem acento e colide (PLAN §38).
 *
 * Sem `VITE_MEDIA_BASE_URL` configurado e sem URL no catálogo, esta origem
 * simplesmente não se aplica, e o app segue funcionando online.
 */
export class AuthorizedDirectMediaSource implements MediaSource {
  readonly id = 'authorized-direct';

  constructor(
    private readonly baseUrl: string = MEDIA_BASE_URL,
    private manifest: MediaManifest | null = null,
    /**
     * Desliga o **palpite** de URL por convenção.
     *
     * Ligado assim que o app tenta carregar o manifest, tenha ele respondido ou
     * não. O motivo é concreto: com a base padrão `/media`, adivinhar
     * `/media/<id>.mp4` numa instalação sem vídeos ofereceria download para os 505
     * sinais e todos dariam 404 — exatamente o "botão que só falha" que esta
     * origem existe para evitar.
     *
     * Não afeta a URL explícita do catálogo, que é informação e não palpite.
     */
    private strict = false,
  ) {}

  /**
   * Injeta o resultado da tentativa de carregar o manifest.
   *
   * `strict` é `true` por padrão porque chamar este método já significa que houve
   * tentativa: `null` aqui quer dizer "a origem não publica nada", não "ainda não
   * sabemos".
   */
  setManifest(manifest: MediaManifest | null, strict = true): void {
    this.manifest = manifest;
    this.strict = strict;
  }

  private urlFor(sign: Sign): string | undefined {
    const item = this.manifest?.items[sign.id];
    if (item) return resolveItemUrl(this.baseUrl, item);

    // URL declarada pelo próprio catálogo continua valendo em qualquer modo.
    if (sign.media.authorizedDownloadUrl) return sign.media.authorizedDownloadUrl;

    if (this.strict || !this.baseUrl) return undefined;
    return `${this.baseUrl}/${encodeURIComponent(sign.id)}.mp4`;
  }

  canDownload(sign: Sign): boolean {
    return this.urlFor(sign) !== undefined;
  }

  resolve(sign: Sign): Promise<MediaResolution> {
    const url = this.urlFor(sign);
    if (!url) return Promise.resolve({});

    // O manifest tem prioridade sobre o catálogo: ele descreve o arquivo que está
    // realmente publicado na origem.
    const item = this.manifest?.items[sign.id];
    const expectedBytes = item?.bytes ?? sign.media.expectedBytes;
    const checksum = item?.sha256 ?? sign.media.checksum;

    return Promise.resolve({
      url,
      mimeType: item?.mimeType ?? sign.media.mimeType ?? 'video/mp4',
      ...(expectedBytes !== undefined ? { expectedBytes } : {}),
      ...(checksum ? { checksum } : {}),
    });
  }
}

/**
 * Arquivo fornecido manualmente pelo usuário (PLAN §23).
 *
 * Não resolve URL remota: o arquivo já está no dispositivo. `canDownload` é `false`
 * porque não há nada a baixar — a importação é um caminho de escrita separado,
 * tratado por `importManualMedia`.
 */
export class ManualMediaSource implements MediaSource {
  readonly id = 'manual';

  canDownload(): boolean {
    return false;
  }

  resolve(): Promise<MediaResolution> {
    return Promise.resolve({});
  }
}

/** Origens disponíveis, na ordem de preferência para download. */
export function defaultMediaSources(): MediaSource[] {
  return [new AuthorizedDirectMediaSource(), new OnlineYoutubeMediaSource(), new ManualMediaSource()];
}
